import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';

type ToastTone = 'success' | 'error' | 'info';

interface ToastEntry {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success(message: string): void;
  error(message: string): void;
  info(message: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 4000;
// Unit D: coalesce bursts. An identical (tone, message) already on screen is
// dropped, and the pile is capped — oldest out — so a retry loop or repeated
// failure can never avalanche the viewport (mirrors the mobile overlay's
// S79 rule).
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = ++idRef.current;
      let added = false;
      setToasts((prev) => {
        if (prev.some((t) => t.tone === tone && t.message === message)) {
          return prev;
        }
        added = true;
        const next = [...prev, { id, tone, message }];
        return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
      });
      if (added) setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

interface ToastViewportProps {
  toasts: ToastEntry[];
  onDismiss(id: number): void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-16 sm:top-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {toasts.map((t) => (
        <Toast key={t.id} entry={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'bg-white border-emerald-200 text-emerald-900',
  error: 'bg-white border-breaker-red/30 text-breaker-red-deep',
  info: 'bg-white border-breaker-line-2 text-breaker-ink',
};

const TONE_INDICATORS: Record<ToastTone, string> = {
  success: 'bg-breaker-green',
  error: 'bg-breaker-red',
  info: 'bg-breaker-identity',
};

function Toast({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  const [entering, setEntering] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto rounded-md border shadow-sm px-4 py-3 text-sm flex items-start gap-3 transition-all duration-150',
        entering ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0',
        TONE_CLASSES[entry.tone],
      )}
    >
      <span
        aria-hidden
        className={cn('mt-1 h-2 w-2 rounded-full shrink-0', TONE_INDICATORS[entry.tone])}
      />
      <p className="flex-1 leading-snug">{entry.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="text-breaker-mute hover:text-breaker-ink transition-colors -mr-1 -my-1 px-1"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
